<template>
  <div class="verify-code">
    <div class="flex-group">
      <span>{{ $t('member-information.key025') }}</span>
      <span class="change_email" @click="changeEmail">
        {{ $t('member-information.key026') }}
      </span>
    </div>
    <input v-model="code" placeholder="請輸入驗證碼" />
    <div class="verify-email flex-group">
      <p class="resend" :class="{ disResend: !canResend }" @click="sendCode">
        <i class="bi bi-arrow-clockwise"></i>
        <span>{{ $t('member-information.key030') }}</span>
        <span v-if="!canResend">&nbsp;{{ countdownResend }}s</span>
      </p>
      <span class="verify-time"> {{ $t('member-information.key031') }} {{ formatTime(countdownVerify) }} </span>
    </div>
    <div class="alert" v-if="error">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, defineExpose, computed, inject } from 'vue';
import { store } from '@/vuex';
import { useI18n } from 'vue-i18n';
import { getCodeToVerifyEmail, updateEmail } from '@/api/apiService';
import { HttpStatusCode } from 'axios';

const props = defineProps({
  verifyShow: {
    type: Boolean,
    required: false,
  },
  email: {
    type: String,
    required: true,
  },
  emailField: {
    type: String,
    default: 'contactEmail',
  },
});

const emit = defineEmits(['success', 'editEmail']);

const loading = inject('loading');
const refreshUserData = inject('refreshUserData');
const { t } = useI18n();
const code = ref('');
const error = ref('');
const countdownVerify = ref(600);
const countdownResend = ref(60);
const canResend = ref(false);
const jwt = computed(() => store.state.jwt);

let verifyTimer = null;
let resendTimer = null;

// 發送驗證碼
const sendCode = async () => {
  try {
    loading.value = true;
    await getCodeToVerifyEmail({ contactEmail: props.email }, jwt.value);
    startCountdown();
    error.value = '';
  } catch (err) {
    emit('editEmail', t('member-information.key097'));
    error.value = t('member-information.key098');
  } finally {
    loading.value = false;
  }
};
// 驗證輸入的驗證碼
const validateCode = async () => {
  try {
    loading.value = true;
    await updateEmail({ [props.emailField]: props.email, code: code.value }, jwt.value, props.emailField);
    // Todo : 為了讓驗證確認後更新資料的流程一致 refreshUserData是跨級傳喚MemberInfo更新UserData資料的方法
    // 較好的方法應該是讓MemberProfile控制流程更新資料 這裡只做驗證回傳結果
    await refreshUserData();
    emit('success', props.email);
    code.value = '';
    error.value = '';
  } catch (err) {
    if (err?.response?.status === HttpStatusCode.BadRequest) {
      error.value = t('register.key029');
    } else {
      console.error('Error updateEmail:', err);
    }
  } finally {
    loading.value = false;
  }
};

const changeEmail = () => {
  stopCountdown();
  resetCountdown();
  emit('editEmail');
  code.value = '';
  error.value = '';
};

const stopCountdown = () => {
  clearInterval(verifyTimer);
  clearInterval(resendTimer);
};

const resetCountdown = () => {
  countdownVerify.value = 600;
  countdownResend.value = 60;
  canResend.value = false;
};

const startCountdown = () => {
  countdownVerify.value = 600;
  if (verifyTimer) clearInterval(verifyTimer);
  verifyTimer = setInterval(() => {
    countdownVerify.value--;
    if (countdownVerify.value <= 0) {
      clearInterval(verifyTimer);
      verifyTimer = null;
      error.value = t('member-information.key099');
    }
  }, 1000);

  // 重新發送倒數時間
  countdownResend.value = 60;
  canResend.value = false;
  if (resendTimer) clearInterval(resendTimer);
  resendTimer = setInterval(() => {
    countdownResend.value--;
    if (countdownResend.value <= 0) {
      clearInterval(resendTimer);
      resendTimer = null;
      canResend.value = true;
    }
  }, 1000);
};

const formatTime = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
};

defineExpose({ sendCode });
watch(code, (newEmail) => {
  if (newEmail.length === 6) {
    validateCode();
  }
});
</script>

<style scoped>
.verify-code {
  width: 100%;
  input {
    width: 100%;
  }
  .flex-group {
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
    span {
      font-size: 15px;
    }

    .change_email {
      color: #e79fff;
      text-decoration: underline;
      text-underline-offset: 5px;
      cursor: pointer;
    }
  }
  .verify-email {
    p,
    span {
      font-size: 15px;
    }
    .resend {
      color: #fff599;
      cursor: pointer;

      span.sendText {
        color: #fff599;
        text-decoration: underline;
        text-underline-offset: 5px;
        cursor: pointer;
      }
    }
    .disResend {
      color: #c7c7c7;
      pointer-events: none;

      span,
      span.sendText {
        color: #c7c7c7;
        pointer-events: none;
      }
    }
    .sendText {
      color: #fff599;
      text-decoration: underline;
      text-underline-offset: 5px;
      cursor: pointer;
    }
    .verify-time {
      color: #c7c7c7;
    }
  }
}
</style>
